import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Reservation {
  id: number;
  conversationId: string;
  productKey: string;
  paymentId?: string;
  price: number;
  paidPrice: number;
  currency: string;
  status: string;
  bookingNumber?: string;
  hotelName?: string;
  roomName?: string;
  checkInDate?: string;
  checkOutDate?: string;
  createdAt: string;
  paymentCompletedAt?: string;
  bookingCompletedAt?: string;
  errorMessage?: string;
}

export interface ReservationsResponse {
  active: Reservation[];
  past: Reservation[];
  all: Reservation[];
}

export interface Coupon {
  id: number;
  code: string;
  title: string;
  description?: string;
  discountType: string;
  discountValue: number;
  minAmount?: number;
  maxDiscount?: number;
  validFrom: string;
  validUntil: string;
  isActive: boolean;
  usageLimit?: number;
  usedCount: number;
  isPublic: boolean;
  revealAfterReservation: boolean;
  codeHidden: boolean;
}

export interface UserCoupon {
  id: number;
  coupon: Coupon;
  assignedAt: string;
  usedAt?: string;
  reservationId?: number;
  isUsed: boolean;
  isExpired: boolean;
}

export interface UserProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phoneNumber?: string;
  createdAt?: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface SavedPassenger {
  id: number;
  firstName: string;
  lastName: string;
  gender?: string;
  tcNumber?: string;
  isForeign: boolean;
  birthDate?: string;
  phoneNumber?: string;
  email?: string;
  isSelf: boolean;
}

export type SavedPassengerRequest = Omit<SavedPassenger, 'id'>;

export interface InvoiceProfile {
  id: number;
  type: 'Personal' | 'Corporate';
  title: string;
  isDefault: boolean;
  tcNumber?: string;
  personalName?: string;
  personalSurname?: string;
  companyName?: string;
  taxOffice?: string;
  taxNumber?: string;
  companyAddress?: string;
  companyCity?: string;
  companyPostalCode?: string;
  companyPhoneNumber?: string;
  companyEmail?: string;
}

export type InvoiceProfileRequest = Omit<InvoiceProfile, 'id'>;

export interface MyCouponsResponse {
  active: UserCoupon[];
  used: UserCoupon[];
  expired: UserCoupon[];
  all: UserCoupon[];
}

@Injectable({ providedIn: 'root' })
export class AccountService {
  private apiUrl = `${environment.apiUrl}/Account`;
  private couponsUrl = `${environment.apiUrl}/Coupons`;

  constructor(private http: HttpClient) {}

  getReservations(status?: string): Observable<ReservationsResponse> {
    const url = status ? `${this.apiUrl}/reservations?status=${encodeURIComponent(status)}` : `${this.apiUrl}/reservations`;
    return this.http.get<ReservationsResponse>(url);
  }

  getReservationById(id: number): Observable<Reservation> {
    return this.http.get<Reservation>(`${this.apiUrl}/reservations/${id}`);
  }

  getMyCoupons(): Observable<MyCouponsResponse> {
    return this.http.get<MyCouponsResponse>(`${this.couponsUrl}/my`);
  }

  redeemCoupon(code: string): Observable<UserCoupon> {
    return this.http.post<UserCoupon>(`${this.couponsUrl}/redeem`, { code });
  }

  getProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.apiUrl}/profile`);
  }

  updateProfile(data: UpdateProfileRequest): Observable<UserProfile> {
    return this.http.put<UserProfile>(`${this.apiUrl}/profile`, data);
  }

  changePassword(data: ChangePasswordRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/change-password`, data);
  }

  getPassengers(): Observable<SavedPassenger[]> {
    return this.http.get<SavedPassenger[]>(`${this.apiUrl}/passengers`);
  }

  addPassenger(data: SavedPassengerRequest): Observable<SavedPassenger> {
    return this.http.post<SavedPassenger>(`${this.apiUrl}/passengers`, data);
  }

  updatePassenger(id: number, data: SavedPassengerRequest): Observable<SavedPassenger> {
    return this.http.put<SavedPassenger>(`${this.apiUrl}/passengers/${id}`, data);
  }

  deletePassenger(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/passengers/${id}`);
  }

  getInvoiceProfiles(): Observable<InvoiceProfile[]> {
    return this.http.get<InvoiceProfile[]>(`${this.apiUrl}/invoice-profiles`);
  }

  addInvoiceProfile(data: InvoiceProfileRequest): Observable<InvoiceProfile> {
    return this.http.post<InvoiceProfile>(`${this.apiUrl}/invoice-profiles`, data);
  }

  updateInvoiceProfile(id: number, data: InvoiceProfileRequest): Observable<InvoiceProfile> {
    return this.http.put<InvoiceProfile>(`${this.apiUrl}/invoice-profiles/${id}`, data);
  }

  deleteInvoiceProfile(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/invoice-profiles/${id}`);
  }
}
